// Layouts
import Layout from "@/components/angle/Layout/Layout.vue";
import { createRouter, createWebHistory } from "vue-router";
import Authentication from "./util/Authentication";
import AppConfig from "./app-config";

const CmsWrapper = () => import("@/views/Pages/CmsWrapper.vue");
const Infogram = () => import("@/components/embeds/Infogram.vue");
const LoginFailed = () => import("@/views/Pages/LoginFailed.vue");

// SingleView
const SingleView = () => import("@/views/SingleView/SingleView.vue");

// SubMenu
// const SubMenu = () => import("@/views/SubMenu/SubMenu.vue");

const savedRouteKey = "savedRoute";

const apiRoot = AppConfig.wpApiRoot;
const cmsUrlLevel1 = `${apiRoot}/wp-json/wp/v2/pages/6194`
const cmsUrlLevel2 = `${apiRoot}/wp-json/wp/v2/pages/6199`

// Helper to wait for Auth0 to be initialized and ready
function waitForAuth0Ready(timeout = 10000) {
  return new Promise((resolve) => {
    const start = Date.now();
    const checkReady = () => {
      const auth0Instance = Authentication.getAuth0Instance();
      
      // If Auth0 is initialized and not loading, we're ready
      if (auth0Instance && !auth0Instance.isLoading.value) {
        resolve(true);
      } 
      // If we've timed out, resolve anyway (app will handle unauthenticated state)
      else if (Date.now() - start > timeout) {
        console.warn('Auth0 initialization timeout');
        resolve(false);
      } 
      // Keep checking
      else {
        setTimeout(checkReady, 50);
      }
    };
    checkReady();
  });
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      // Default route
      path: "/",
      component: Layout,
      meta: {requiresAuth: false}, // Public by default
      redirect: "/risk-factor/risk_factors_size",
      children: [
        {
          path: "/infogram",
          component: Infogram,
          meta: {requiresAuth: false},
        },

        // Tearsheet pages
        {
          path: "/tearsheets",
          redirect: "/risk-factor/risk_factors_size",
        },
        {
          path: "/signal/:signalName",
          component: SingleView,
          meta: {requiresAuth: true}, // ✅ SIGNALS require login
        },
        {
          path: "/risk-factor/:signalName",
          meta: {requiresAuth: false}, // ✅ RISK FACTORS are public
          component: SingleView,
        },
      ],
    },
    {
      path: "/landing",
      component: CmsWrapper,
      beforeEnter: async (to, from, next) => {
        await waitForAuth0Ready();
        
        if (Authentication.isUserAuthenticated()) {
          next();
        } else {
          // Redirect to login with Auth0
          await Authentication.login();
        }
      },
      meta: {requiresAuth: false},
      props: () => {
        let cmsUrl;
        if (Authentication.isUserSubscriber()) {
          // if user is a subscriber, go to level 2 page
          cmsUrl = cmsUrlLevel2
        } else {
          // go to the prospect page
          cmsUrl = cmsUrlLevel1
        }
        return {
          cmsUrl
        };
      },
    },
    // the next few paths are so that we can specify which of the landing pages we want
    {
      path: "/level1",
      component: CmsWrapper,
      meta: {requiresAuth: false},
      props: {
        cmsUrl:
        cmsUrlLevel1,
      },
    },
    {
      path: "/level2",
      component: CmsWrapper,
      meta: {requiresAuth: true},
      props: {
        cmsUrl:
        cmsUrlLevel2,
      },
    },
    {
      // Auth0 Login - trigger authentication
      path: "/login",
      meta: {requiresAuth: false},
      beforeEnter: async () => {
        await Authentication.login();
      },
    },
    {
      // Auth0 Logout
      path: "/logout",
      meta: {requiresAuth: false},
      beforeEnter: async () => {
        localStorage.removeItem(savedRouteKey);
        await Authentication.logout();
      },
    },
    {
      // Login failed page
      path: "/login-failed",
      meta: {requiresAuth: false},
      component: LoginFailed
    },
    {
      // Not found route - Vue Router 4 syntax
      path: "/:pathMatch(.*)*",
      redirect: "/",
    },
  ],
});

router.beforeEach(checkIfAuthenticated);

/**
 * Guard that any pages have users logged in, unless they are public
 * @param {*} to
 * @param {*} from
 * @param {*} next
 */
async function checkIfAuthenticated(to, from, next) {
  // Wait for Auth0 to be initialized (wait for UserBlock to mount)
  await waitForAuth0Ready();

  // Check if route requires authentication
  if (to.meta.requiresAuth !== false && !Authentication.isUserAuthenticated()) {
    const auth0Instance = Authentication.getAuth0Instance();
    
    if (!auth0Instance) {
      // Auth0 failed to initialize - show error
      console.error('Auth0 not initialized - cannot authenticate');
      next('/login-failed');
      return;
    }
    
    // Save the intended destination
    localStorage.setItem(savedRouteKey, to.path);
    
    // Trigger Auth0 login
    await Authentication.login();
    
    // The login will redirect, so we don't need to call next()
    // But we call it anyway to prevent router errors
    next(false);
  } else {
    next();
  }
}

export default router;
